import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { getUserPhotoUrl } from '../utils/userPhoto';
import AssistantWidget from './AssistantWidget';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const MQ_COMPACT_NAV = '(max-width: 900px)';
const MQ_AVATAR_ONLY = '(max-width: 580px)';
const MQ_USER_ACCOUNT_POPOVER = '(max-width: 960px)';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const { user, logout } = authContext || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [navClusterOpen, setNavClusterOpen] = useState<boolean>(false);
  const [useCompactNav, setUseCompactNav] = useState<boolean>(false);
  const [avatarOnlyUser, setAvatarOnlyUser] = useState<boolean>(false);
  const [useUserPopover, setUseUserPopover] = useState<boolean>(false);
  const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
  const navClusterRef = useRef<HTMLDivElement | null>(null);
  const userPopoverRef = useRef<HTMLDivElement | null>(null);

  const userInitials = useMemo(() => {
    if (!user?.name) return 'F';

    return user.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }, [user?.name]);

  const roleLabel = useMemo(() => {
    switch (user?.role) {
      case 'admin':
        return 'Administrador';
      case 'manager':
        return 'Gestor';
      case 'developer':
        return 'Desenvolvedor';
      case 'hr':
        return 'RH';
      default:
        return 'Equipe';
    }
  }, [user?.role]);

  const userPhotoUrl = useMemo(() => getUserPhotoUrl(user), [user]);

  const showUsersLink = user?.role === 'admin' || user?.role === 'hr';
  const showProfileLink = Boolean(user && user.role !== 'admin');
  const isEmployee = user?.role === 'developer';

  const compactNavTriggerLabel = useMemo((): string => {
    const path = location.pathname;

    if (path === '/' || path === '') {
      return 'Dashboard';
    }
    if (path.startsWith('/financeiro/folha')) {
      return 'Folha de pagamento';
    }
    if (path.startsWith('/financeiro/cobrancas')) {
      return 'Cobranças';
    }
    if (path.startsWith('/financeiro/mensal')) {
      return 'Planilha mensal';
    }
    if (path.startsWith('/financeiro/bandas-orcamento')) {
      return 'Bandas de orçamento';
    }
    if (path.startsWith('/financeiro')) {
      return 'Financeiro';
    }
    if (path.startsWith('/cycles')) {
      return 'Ciclos';
    }
    if (path.startsWith('/reports')) {
      return 'Relatórios';
    }
    if (path.startsWith('/users')) {
      return 'Usuários';
    }
    if (path.startsWith('/meu-perfil')) {
      return 'Meu perfil';
    }

    return 'Dashboard';
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mqNav = window.matchMedia(MQ_COMPACT_NAV);
    const mqAvatar = window.matchMedia(MQ_AVATAR_ONLY);
    const mqUserPopover = window.matchMedia(MQ_USER_ACCOUNT_POPOVER);

    const sync = (): void => {
      setUseCompactNav(mqNav.matches);
      setAvatarOnlyUser(mqAvatar.matches);
      setUseUserPopover(mqUserPopover.matches);
    };

    sync();
    mqNav.addEventListener('change', sync);
    mqAvatar.addEventListener('change', sync);
    mqUserPopover.addEventListener('change', sync);

    return () => {
      mqNav.removeEventListener('change', sync);
      mqAvatar.removeEventListener('change', sync);
      mqUserPopover.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    if (!useUserPopover) {
      setUserMenuOpen(false);
    }
  }, [useUserPopover]);

  useEffect(() => {
    setNavClusterOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navClusterOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const el = navClusterRef.current;
      if (el && !el.contains(event.target as Node)) {
        setNavClusterOpen(false);
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setNavClusterOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [navClusterOpen]);

  useEffect(() => {
    if (!userMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const el = userPopoverRef.current;
      if (el && !el.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const handleLogout = (): void => {
    if (logout) {
      logout();
      navigate('/login');
    }
  };

  const closeNavCluster = (): void => {
    setNavClusterOpen(false);
  };

  const year = new Date().getFullYear();

  const userCardClass = [
    'app-user-card',
    useUserPopover ? 'app-user-card--popover' : '',
    useUserPopover && avatarOnlyUser ? 'app-user-card--avatar-only' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const mainNavLinks = (
    <>
      {!isEmployee ? (
        <NavLink to="/" end className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}>
          Dashboard
        </NavLink>
      ) : null}
      <NavLink
        to="/financeiro"
        end
        className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}
      >
        Financeiro
      </NavLink>
      <NavLink
        to="/financeiro/folha"
        className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}
      >
        Folha de pagamento
      </NavLink>
      {!isEmployee ? (
        <NavLink
          to="/financeiro/cobrancas"
          className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}
        >
          Cobranças
        </NavLink>
      ) : null}
      {!isEmployee ? (
        <NavLink
          to="/financeiro/mensal"
          className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}
        >
          Planilha mensal
        </NavLink>
      ) : null}
      {!isEmployee ? (
        <NavLink
          to="/financeiro/bandas-orcamento"
          className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}
        >
          Bandas de orçamento
        </NavLink>
      ) : null}
      <NavLink to="/cycles" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}>
        Ciclos
      </NavLink>
      {!isEmployee ? (
        <NavLink to="/reports" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}>
          Relatórios
        </NavLink>
      ) : null}
      {showUsersLink ? (
        <NavLink to="/users" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}>
          Usuários
        </NavLink>
      ) : null}
      {showProfileLink ? (
        <NavLink
          to="/meu-perfil"
          className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}
        >
          Meu perfil
        </NavLink>
      ) : null}
    </>
  );

  const clusterMenuItems = (
    <>
      {!isEmployee ? (
        <NavLink
          to="/"
          end
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Dashboard
        </NavLink>
      ) : null}
      <NavLink
        to="/financeiro"
        end
        role="menuitem"
        className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
        onClick={closeNavCluster}
      >
        Financeiro
      </NavLink>
      <NavLink
        to="/financeiro/folha"
        role="menuitem"
        className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
        onClick={closeNavCluster}
      >
        Folha de pagamento
      </NavLink>
      {!isEmployee ? (
        <NavLink
          to="/financeiro/cobrancas"
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Cobranças
        </NavLink>
      ) : null}
      {!isEmployee ? (
        <NavLink
          to="/financeiro/mensal"
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Planilha mensal
        </NavLink>
      ) : null}
      {!isEmployee ? (
        <NavLink
          to="/financeiro/bandas-orcamento"
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Bandas de orçamento
        </NavLink>
      ) : null}
      <NavLink
        to="/cycles"
        role="menuitem"
        className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
        onClick={closeNavCluster}
      >
        Ciclos
      </NavLink>
      {!isEmployee ? (
        <NavLink
          to="/reports"
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Relatórios
        </NavLink>
      ) : null}
      {showUsersLink ? (
        <NavLink
          to="/users"
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Usuários
        </NavLink>
      ) : null}
      {showProfileLink ? (
        <NavLink
          to="/meu-perfil"
          role="menuitem"
          className={({ isActive }) => `app-nav-cluster-link${isActive ? ' is-active' : ''}`}
          onClick={closeNavCluster}
        >
          Meu perfil
        </NavLink>
      ) : null}
    </>
  );

  return (
    <div className="layout-shell">
      <header className="app-header">
        <div
          className="app-header-inner"
          data-compact-nav={useCompactNav ? 'true' : undefined}
          data-avatar-only={useUserPopover && avatarOnlyUser ? 'true' : undefined}
          data-user-popover={useUserPopover ? 'true' : undefined}
          data-employee-nav={isEmployee ? 'true' : undefined}
        >
          <NavLink to={isEmployee ? '/financeiro' : '/'} className="app-brand">
            <span className="app-brand-mark">F</span>
            <span className="app-brand-copy">
              <strong>Financeiro</strong>
              <small>{user?.tenant?.name ?? 'Gestão de performance'}</small>
            </span>
          </NavLink>

          <div className="app-nav-panel">
            {useCompactNav ? (
              <div className="app-nav-cluster" ref={navClusterRef}>
                <button
                  type="button"
                  id="app-nav-cluster-trigger"
                  className={`app-nav-cluster-trigger${navClusterOpen ? ' is-open' : ''}`}
                  aria-expanded={navClusterOpen}
                  aria-haspopup="true"
                  aria-controls="app-nav-cluster-panel"
                  aria-label={`Menu de navegação. Secção atual: ${compactNavTriggerLabel}`}
                  onClick={() => {
                    setNavClusterOpen((open) => !open);
                    setUserMenuOpen(false);
                  }}
                >
                  <span className="app-nav-cluster-trigger-label">
                    {compactNavTriggerLabel}
                  </span>
                  <span className="app-nav-cluster-chevron" aria-hidden />
                </button>
                {navClusterOpen ? (
                  <div id="app-nav-cluster-panel" className="app-nav-cluster-menu" role="menu">
                    {clusterMenuItems}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="app-nav-links-wrap">
                <div className="app-nav-main-rail">
                  <nav className="app-nav-links">{mainNavLinks}</nav>
                </div>
              </div>
            )}

            {useUserPopover ? (
              <div className={userCardClass} ref={userPopoverRef}>
                <button
                  type="button"
                  className="app-user-photo-trigger"
                  onClick={() => {
                    setUserMenuOpen((open) => !open);
                    setNavClusterOpen(false);
                  }}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  aria-controls="app-user-popover-panel"
                  id="app-user-photo-trigger"
                  aria-label="Abrir menu da conta: nome e terminar sessão"
                >
                  {userPhotoUrl ? (
                    <img src={userPhotoUrl} alt="" className="app-user-avatar-image" />
                  ) : (
                    <div className="app-user-avatar" aria-hidden="true">
                      {userInitials}
                    </div>
                  )}
                </button>
                {userMenuOpen ? (
                  <div id="app-user-popover-panel" className="app-user-popover" role="menu">
                    <div className="app-user-popover-head">
                      <strong>{user?.name}</strong>
                      <span>{roleLabel}</span>
                    </div>
                    <button
                      type="button"
                      className="app-user-popover-logout"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                    >
                      Sair
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className={userCardClass}>
                {userPhotoUrl ? (
                  <img
                    src={userPhotoUrl}
                    alt={user?.name || 'Usuário'}
                    className="app-user-avatar-image"
                  />
                ) : (
                  <div className="app-user-avatar" aria-hidden="true">
                    {userInitials}
                  </div>
                )}
                <div className="app-user-meta">
                  <strong>{user?.name}</strong>
                  <span>{roleLabel}</span>
                </div>
                <button type="button" onClick={handleLogout} className="app-logout-button">
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="app-main-inner">{children}</div>
      </main>

      <footer className="app-footer">
        <div className="app-footer-inner">
          <div className="app-footer-minimal">
            <NavLink to="/" className="app-footer-brand">
              <span className="app-footer-mark" aria-hidden="true">
                F
              </span>
              <span className="app-footer-brand-text">
                <strong>Financeiro</strong>
                <small>Gestão de performance</small>
              </span>
            </NavLink>

            <p className="app-footer-description">Plataforma financeira</p>

            <p className="app-footer-copy">
              © {year} Financeiro. Operação financeira centralizada e visão consolidada da
              área.
            </p>
          </div>
        </div>
      </footer>

      <AssistantWidget />
    </div>
  );
};

export default Layout;
